import React, { useState, useEffect, useRef } from 'react';
import { Table, Card, message, Form, Select, Cascader, Button, Modal, Input, Upload, Popconfirm, Row, Col, Image } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import ResizableTable from '../components/ResizableTable';

const { Option, OptGroup } = Select;
const { TextArea } = Input;

const Course = ({ user }) => {
  const [courseStageOptions, setCourseStageOptions] = useState([]); // 适合年龄选项
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [isCreating, setIsCreating] = useState(false);
  const [searchForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [uploadedPicUrl, setUploadedPicUrl] = useState('');
  const [uploadedAttachmentId, setUploadedAttachmentId] = useState('');
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState('');
  const [courseTypeOptions, setCourseTypeOptions] = useState([]); // 应用场景枚举
  const [courseLabelOptions, setCourseLabelOptions] = useState([]); // 运动项目枚举
  const [showCopyButton, setShowCopyButton] = useState(true); // 控制"同运动项目"按钮显示状态
  const courseNameInputRef = useRef(null); // 引用动作名称输入框
  const { apiClient } = useAuth();

  // 获取适合年龄选项
  const fetchCourseStageOptions = async () => {
    try {
      const response = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'COURSE_AGE',
        colName: 'COURSE_AGE'
      });
      
      if (response.data && response.data.code === 200) {
        let rawData = response.data.data;
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
        }
        
        const options = (rawData || []).map((item) => ({
          label: item.enumName,
          value: item.enumCode || item.enumName,
        }));
        
        setCourseStageOptions(options);
      } else {
        console.error('获取适合年龄选项失败:', response.data?.msg || '未知错误');
      }
    } catch (error) {
      console.error('获取适合年龄选项异常:', error);
      message.error('获取适合年龄选项失败');
    }
  };

  const [columns, setColumns] = useState([
    {
      title: '预览照片',
      dataIndex: 'picUrl',
      key: 'picUrl',
      width: 120,
      render: (picUrl) =>
        picUrl ? <Image src={picUrl} alt="pic" width={100} height={100} /> : null,
    },
    {
      title: '运动项目',
      dataIndex: 'eventTitle',
      key: 'eventTitle',
      width: 120,
      filters: courseLabelOptions.reduce((acc, opt) => {
        acc.push({ text: opt.label, value: opt.value });
        if (opt.children) {
          opt.children.forEach(child => {
            acc.push({ text: `${opt.label}/${child.label}`, value: `${opt.value}/${child.value}` });
          });
        }
        return acc;
      }, []),
      onFilter: (value, record) => {
        // 检查完整路径匹配
        if (record.eventTitle === value) return true;
        // 检查是否为父子级联路径格式
        const [parentValue, childValue] = value.split('/');
        if (childValue && record.eventTitle === childValue) return true;
        // 检查记录中的级联路径是否匹配
        if (typeof record.eventTitle === 'string' && record.eventTitle.includes('/')) {
          const [recordParentValue, recordChildValue] = record.eventTitle.split('/');
          if (value === `${recordParentValue}/${recordChildValue}`) return true;
        }
        return false;
      },
      render: (text) => {
        // 如果包含/，则是父子级联路径，直接按/分割显示
        if (typeof text === 'string' && text.includes('/')) {
          const [parentValue, childValue] = text.split('/');
          for (const opt of courseLabelOptions) {
            if (opt.value === parentValue && opt.children) {
              const child = opt.children.find(c => c.value === childValue);
              if (child) return `${opt.label}/${child.label}`;
            }
          }
        }

        // 否则按原有逻辑处理单个值
        for (const opt of courseLabelOptions) {
          if (opt.value === text) return opt.label;
          if (opt.children) {
            const child = opt.children.find(c => c.value === text);
            if (child) return `${opt.label}/${child.label}`;
          }
        }
        return text || '无';
      },
    },
    {
      title: '动作名称',
      dataIndex: 'courseName',
      key: 'courseName',
      width: 150,
      sorter: (a, b) => {
        const nameA = a.courseName || '';
        const nameB = b.courseName || '';
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: '应用场景',
      dataIndex: 'courseKbn',
      key: 'courseKbn',
      width: 120,
      filters: courseTypeOptions.map(opt => ({ text: opt.label, value: opt.value })),
      onFilter: (value, record) => record.courseKbn === value,
      render: (text) => findLabelByValue(courseTypeOptions, text) || text || '无',
    },

    {
      title: '适合年龄',
      dataIndex: 'courseStage',
      key: 'courseStage',
      width: 100,
      filters: courseStageOptions.map(opt => ({ text: opt.label, value: opt.value })),
      onFilter: (value, record) => record.courseStage === value,
      render: (text) => {
        const option = courseStageOptions.find(opt => opt.value === text);
        return option ? option.label : text || '无';
      },
    },
    {
      title: '是否公开',
      dataIndex: 'publicOrNot',
      key: 'publicOrNot',
      width: 100,
      filters: [
        { text: '公开', value: '1' },
        { text: '不公开', value: '0' },
      ],
      onFilter: (value, record) => String(record.publicOrNot) === value,
      render: (value) => {
        if (String(value) === '1') return '公开';
        if (String(value) === '0') return '不公开';
        return '-';
      },
    },
    {
      title: '难度',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (text) => {
        if (text === 'easy') return '简单';
        if (text === 'medium') return '中等';
        if (text === 'hard') return '困难';
        return text || '无';
      },
    },
    {
      title: '时长',
      dataIndex: 'eventTime',
      key: 'eventTime',
      width: 100,
    },
    {
      title: '动作介绍',
      dataIndex: 'courseContext',
      key: 'courseContext',
      width: 200,
      ellipsis: true,
    },
    {
      title: '注意事项',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <>
          <Button type="link" icon={<EditOutlined />} onClick={() => showEditModal(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除?" onConfirm={() => handleDelete(record.seq)}>
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ]);

  // 从 options 中根据 value 查找 label
  const findLabelByValue = (options, value) => {
    if (!value) return value;
    const found = (options || []).find((opt) => opt.value === value);
    return found ? found.label : value;
  };

  // Fetch enum options for course type and label
  useEffect(() => {
    if (!apiClient) return;

    const fetchCourseTypeOptions = async () => {
      try {
        const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
          tableName: 'COURSE_TYPE',
          colName: 'COURSE_TYPE',
        });
        if (resp.data && resp.data.code === 200 && resp.data.data) {
          let raw = resp.data.data;
          if (typeof raw === 'string') {
            try {
              raw = JSON.parse(raw);
            } catch (e) {
              console.error('解析应用场景枚举失败:', e);
              message.error('解析应用场景配置失败');
              return;
            }
          }
          const options = (raw || []).map((item) => ({
            label: item.enumName,
            value: item.enumCode || item.enumName,
          }));
          setCourseTypeOptions(options);
        } else {
          message.error(resp.data?.msg || '获取应用场景失败');
        }
      } catch (e) {
        console.error('获取应用场景失败:', e);
        message.error('获取应用场景失败');
      }
    };

    const fetchCourseLabelOptions = async () => {
      try {
        const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', { tableName: 'SPORTS_ITEM' });
        if (resp.data && resp.data.code === 200 && resp.data.data) {
          let raw = resp.data.data;
          if (typeof raw === 'string') {
            try {
              raw = JSON.parse(raw);
            } catch (e) {
              console.error('解析运动项目枚举失败:', e);
              message.error('解析运动项目配置失败');
              return;
            }
          }
          const options = (raw || []).map((p) => ({
            label: p.enumName,
            value: p.enumName,
            children: (p.items || []).map((c) => ({
              label: c.enumName,
              value: c.enumName,
            })),
          }));
          setCourseLabelOptions(options);
        } else {
          message.error(resp.data?.msg || '获取运动项目失败');
        }
      } catch (e) {
        console.error('获取运动项目失败:', e);
        message.error('获取运动项目失败');
      }
    };

    fetchCourseTypeOptions();
    fetchCourseLabelOptions();
    fetchCourseStageOptions();
  }, [apiClient]);

  // Update columns when options change
  useEffect(() => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === 'courseKbn') {
          return {
            ...col,
            filters: courseTypeOptions.map((opt) => ({ text: opt.label, value: opt.value })),
          };
        }
        if (col.key === 'eventTitle') {
          return {
            ...col,
            filters: courseLabelOptions.map((opt) => ({ text: opt.label, value: opt.value })),
          };
        }
        if (col.key === 'courseStage') {
          return {
            ...col,
            filters: courseStageOptions.map((opt) => ({ text: opt.label, value: opt.value })),
          };
        }
        return col;
      })
    );
  }, [courseTypeOptions, courseLabelOptions, courseStageOptions]);

  // Fetch courses
  const fetchCourses = async (params = {}) => {
    setLoading(true);
    try {
      let eventTitleValue = params.eventTitle || '';
      // 如果eventTitle是数组（来自Cascader），用/拼接成字符串
      if (Array.isArray(eventTitleValue)) {
        eventTitleValue = eventTitleValue.join('/');
      }
      
      const response = await apiClient.post('/sports-server-prod/taller.api.v1/course/pageList', {
        courseName: params.courseName || '',
        eventTitle: eventTitleValue,
        courseStage: params.courseStage || '',
        courseKbn: params.courseKbn || '',
        publicOrNot: params.publicOrNot || '',
        pageNo: params.current || 1,
        pageSize: params.pageSize || 10,
      });

      if (response.data && response.data.code === 200) {
        const list = response.data.data.list || [];
        setCourses(list);
        setPagination((prev) => ({
          ...prev,
          current: params.current || 1,
          pageSize: params.pageSize || 10,
          total: response.data.data.totalRecord,
        }));
      } else {
        message.error('获取课程列表失败');
        setCourses([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
      }
    } catch (error) {
      console.error('获取课程列表失败:', error);
      message.error('获取课程列表失败');
      setCourses([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // Handle table change
  const handleTableChange = (newPagination) => {
    const tableValues = searchForm.getFieldsValue();
    const tableMappedValues = mapFormValuesToApi(tableValues);
    fetchCourses({
      ...tableMappedValues,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  // Search
  const handleSearch = () => {
    const searchValues = searchForm.getFieldsValue();
    const searchMappedValues = mapFormValuesToApi(searchValues);
    fetchCourses({
      ...searchMappedValues,
      current: 1,
    });
  };

  // Reset
  const handleReset = () => {
    searchForm.resetFields();
    const resetValues = searchForm.getFieldsValue();
    const resetMappedValues = mapFormValuesToApi(resetValues);
    fetchCourses({ ...resetMappedValues, current: 1, pageSize: pagination.pageSize });
  };

  // Add function to map form values back to API format
  const mapFormValuesToApi = (values) => {
    const mappedValues = { ...values };
    if (values.level) {
      mappedValues.level = values.level === '简单' ? 'easy' :
        values.level === '中等' ? 'medium' :
          values.level === '困难' ? 'hard' : values.level;
    }
    // 如果eventTitle是数组（来自Cascader），用/拼接成字符串
    if (Array.isArray(mappedValues.eventTitle)) {
      mappedValues.eventTitle = mappedValues.eventTitle.join('/');
    }
    return mappedValues;
  };

  // Add function to map API values to form values
  const mapApiValuesToForm = (record) => {
    const mappedRecord = { ...record };
    if (record.level) {
      mappedRecord.level = mapLevelForForm(record.level);
    }
    if (record.courseKbn) {
      mappedRecord.courseKbn = record.courseKbn;
    }
    if (record.eventTitle) {
      // 为Cascader格式化eventTitle
      mappedRecord.eventTitle = findCascaderPathByValue(record.eventTitle);
    }
    if (record.publicOrNot !== undefined && record.publicOrNot !== null) {
      mappedRecord.publicOrNot = String(record.publicOrNot);
    }
    return mappedRecord;
  };

  // Add helper functions to map values for form
  const mapLevelForForm = (value) => {
    if (value === 'easy') return '简单';
    if (value === 'medium') return '中等';
    if (value === 'hard') return '困难';
    return value;
  };

  // Find cascader path by value
  const findCascaderPathByValue = (value) => {
    // 如果值包含/，则按/分割并查找对应的父子级联路径
    if (typeof value === 'string' && value.includes('/')) {
      const [parentValue, childValue] = value.split('/');
      for (const opt of courseLabelOptions) {
        if (opt.value === parentValue && opt.children) {
          const child = opt.children.find(c => c.value === childValue);
          if (child) return [opt.value, child.value];
        }
      }
    }
    
    for (const opt of courseLabelOptions) {
      if (opt.value === value) return [opt.value];
      if (opt.children) {
        const child = opt.children.find(c => c.value === value);
        if (child) return [opt.value, child.value];
      }
    }
    return value;
  };

  // Show edit modal
  const showEditModal = (record = null) => {
    setIsCreating(!record);
    if (record) {
      const mappedRecord = mapApiValuesToForm(record);
      editForm.setFieldsValue(mappedRecord);
      setUploadedPicUrl(record.picUrl || '');

      // 从 attachments 中取最新的视频
      const attachments = record.attachments || [];
      if (attachments.length > 0) {
        const latest = attachments[attachments.length - 1];
        setUploadedAttachmentId(latest.attachmentId || latest.id || '');
        setUploadedVideoUrl(latest.fileAddress || '');
      } else {
        setUploadedAttachmentId('');
        setUploadedVideoUrl('');
      }
      // 编辑模式下，如果动作名称已存在，则不显示"同运动项目"按钮
      setShowCopyButton(!mappedRecord.courseName);
    } else {
      editForm.resetFields();
      // 默认公开
      // 如果有应用场景选项，则默认选择第一个
      // 如果有适合年龄选项，则默认选择第一个
      const defaultValues = { publicOrNot: '1' };
      if (courseTypeOptions && courseTypeOptions.length > 0) {
        defaultValues.courseKbn = courseTypeOptions[0].value;
      }
      if (courseStageOptions && courseStageOptions.length > 0) {
        defaultValues.courseStage = courseStageOptions[0].value;
      }
      editForm.setFieldsValue(defaultValues);
      setUploadedPicUrl('');
      setUploadedAttachmentId('');
      setUploadedVideoUrl('');
      // 创建模式下，初始时动作名称为空，显示"同运动项目"按钮
      setShowCopyButton(true);
    }
    setEditModalVisible(true);
  };

  // Save course
  const handleSave = async () => {
    try {
      const values = await editForm.validateFields();
      const mappedValues = mapFormValuesToApi(values);
      const payload = {
        ...mappedValues,
        picUrl: uploadedPicUrl,
        attachmentId: uploadedAttachmentId,
      };
      if (isCreating) {
        await apiClient.post('/sports-server-prod/taller.api.v1/course/add', payload);
        message.success('添加成功');
      } else {
        await apiClient.post('/sports-server-prod/taller.api.v1/course/update', payload, { params: { seq: mappedValues.seq } });
        message.success('更新成功');
      }
      setEditModalVisible(false);
      const refreshValues = searchForm.getFieldsValue();
      const refreshMappedValues = mapFormValuesToApi(refreshValues);
      fetchCourses({ ...refreshMappedValues, current: pagination.current, pageSize: pagination.pageSize });
    } catch (error) {
      console.error('保存课程失败:', error);
      message.error('操作失败');
    }
  };

  // Delete course
  const handleDelete = async (seq) => {
    try {
      await apiClient.post('/sports-server-prod/taller.api.v1/course/delete', {}, { params: { seq } });
      message.success('删除成功');
      const refreshValues = searchForm.getFieldsValue();
      const refreshMappedValues = mapFormValuesToApi(refreshValues);
      fetchCourses({ ...refreshMappedValues, current: pagination.current, pageSize: pagination.pageSize });
    } catch (error) {
      console.error('删除课程失败:', error);
      message.error('删除失败');
    }
  };

  // Upload props
  const uploadProps = {
    customRequest: async ({ file, onSuccess, onError }) => {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await apiClient.post('/sports-server-prod/sys.api.v1/file/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        onSuccess(response.data);
      } catch (error) {
        onError(error);
      }
    },
    onChange: (info) => {
      if (info.file.status === 'done') {
        message.success('上传成功');
        const data = info.file.response.data;
        const key = Object.keys(data)[0];
        const value = data[key];
        if (info.file.type && info.file.type.startsWith('video/')) {
          // 替换旧视频，只保留最新
          setUploadedAttachmentId(key);
          setUploadedVideoUrl(value);
        } else {
          // 替换旧图片，只保留最新
          setUploadedPicUrl(value);
        }
      } else if (info.file.status === 'error') {
        message.error('上传失败');
      }
    },
    maxCount: 1,
    showUploadList: false,
  };


  return (
    <Card title="资源管理">
      <Form form={searchForm} layout="vertical" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="courseName" label="动作名称">
              <Input placeholder="请输入动作名称" maxLength={20} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="courseKbn" label="应用场景">
              <Select allowClear placeholder="请选择应用场景" style={{ width: '100%' }}>
                {courseTypeOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="eventTitle" label="运动项目">
              <Cascader
                allowClear
                placeholder="请选择运动项目"
                options={courseLabelOptions.map(opt => ({
                  label: opt.label,
                  value: opt.value,
                  children: opt.children ? opt.children.map(child => ({
                    label: child.label,
                    value: child.value,
                  })) : undefined,
                }))}
                style={{ width: '100%' }}
                showSearch
                expandTrigger="hover"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="courseStage" label="适合年龄">
              <Select allowClear placeholder="请选择适合年龄" style={{ width: '100%' }}>
                {courseStageOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="publicOrNot" label="是否公开">
              <Select allowClear placeholder="请选择" style={{ width: '100%' }}>
                <Option value="1">公开</Option>
                <Option value="0">不公开</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={18}>
            {(() => {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              return user.role === 'R00001' ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => showEditModal()}
                >
                  添加动作
                </Button>
              ) : null;
            })()}
          </Col>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              搜索
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={handleReset}>
              重置
            </Button>
          </Col>
        </Row>
      </Form>
      <ResizableTable
        columns={columns}
        dataSource={courses}
        rowKey="seq"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        onColumnsChange={setColumns}
      />
      <Modal
        title={isCreating ? '添加动作' : '编辑动作'}
        open={editModalVisible}
        onOk={handleSave}
        onCancel={() => setEditModalVisible(false)}
        width={800}
      >
        <Form 
          form={editForm} 
          layout="vertical"
          onValuesChange={(changedValues, allValues) => {
            // 当动作名称发生变化时，更新按钮的显示状态
            if (changedValues.hasOwnProperty('courseName')) {
              setShowCopyButton(!allValues.courseName);
            }
          }}
        >
          {/* 隐藏的seq字段 */}
          <Form.Item name="seq" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="eventTitle" 
                label="运动项目"
                rules={[{ required: true, message: '请选择运动项目' }]}
              >
                <Cascader
                    allowClear
                    placeholder="请选择运动项目"
                    options={courseLabelOptions.map(opt => ({
                      label: opt.label,
                      value: opt.value,
                      children: opt.children ? opt.children.map(child => ({
                        label: child.label,
                        value: child.value,
                      })) : undefined,
                    }))}
                    showSearch
                    expandTrigger="hover"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="courseName"
                label="动作名称"
                rules={[
                  { required: true, message: '请输入动作名称' },
                  { max: 20, message: '动作名称最多20个字符' },
                ]}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Input 
                    ref={courseNameInputRef}
                    maxLength={20} 
                    showCount 
                    style={{ flex: 1 }} 
                  />
                </div>
              </Form.Item>
            </Col>

          </Row>
          <Row gutter={16}>

            <Col span={12}>
              <Form.Item name="courseStage" label="适合年龄">
                <Select allowClear placeholder="请选择适合年龄">
                  {courseStageOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                  name="courseKbn"
                  label="应用场景"
                  rules={[{ required: true, message: '请选择应用场景' }]}
              >
                <Select allowClear placeholder="请选择应用场景">
                  {courseTypeOptions.map((opt, index) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="level" label="难易度">
                <Select>
                  <Option value="easy">简单</Option>
                  <Option value="medium">中等</Option>
                  <Option value="hard">困难</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="eventTime"
                label="时长"
                rules={[{ max: 20, message: '时长最多20个字符' }]}
              >
                <Input maxLength={20} showCount />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="publicOrNot"
                label="是否公开"
                rules={[{ required: true, message: '请选择是否公开' }]}
              >
                <Select placeholder="请选择">
                  <Option value="1">公开</Option>
                  <Option value="0">不公开</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="courseContext"
            label="动作介绍"
            rules={[{ max: 200, message: '动作介绍最多200个字符' }]}
          >
            <TextArea rows={4} maxLength={200} showCount />
          </Form.Item>
          <Form.Item
            name="remark"
            label="注意事项"
            rules={[{ max: 100, message: '注意事项最多100个字符' }]}
          >
            <TextArea rows={4} maxLength={100} showCount />
          </Form.Item>
          <Form.Item label="预览照片">
            <Upload {...uploadProps} accept="image/*">
              <Button icon={<UploadOutlined />}>上传图片</Button>
            </Upload>
            {uploadedPicUrl && (
              <img
                src={uploadedPicUrl}
                alt="preview"
                style={{ maxWidth: 200, marginTop: 10 }}
              />
            )}
          </Form.Item>
          <Form.Item label="视频附件">
            <Upload {...uploadProps} accept="video/*">
              <Button icon={<UploadOutlined />}>上传视频</Button>
            </Upload>
            {uploadedVideoUrl && (
              <video
                src={uploadedVideoUrl}
                controls
                style={{ maxWidth: 200, marginTop: 10 }}
              />
            )}
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Course;
